import { ConnectedProps } from "react-redux";
import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { connector } from "./redux";
import { ClavaContext } from "../../../config/contexts";
import {MatchLocationEnum, StandingSmall } from "../../../client/api";
import { showTranslated, translate } from "../../../config/translator";
import EmblemImage from "../EmblemImage";
import RoundedRectangle from "../RoundedRectangle";
import { IDType } from "../../../config/types";
import { getTeamHighlightColor, isAdmin } from "../../../config/utils";
import { pathTeamView } from "../../../config/routes";

const Line: React.FC<{ standing: StandingSmall; onTeamClick: () => void; isMatchTeam?: boolean }> = ({
                                                                                                                                standing,
                                                                                                                                onTeamClick,
                                                                                                                                isMatchTeam
                                                                                                                              }) => {
  const { l, user } = useContext(ClavaContext);
  const showIds = useMemo(() => localStorage.getItem("showIds") === "yes", []);
  const { style: teamHighlightStyle, fallbackClass } = getTeamHighlightColor(
    standing.team.primaryColor,
    standing.team.secondaryColor
  );
  const highlightStyle = isMatchTeam ? teamHighlightStyle : undefined;
  const baseRowClass =
    "border-b border-b-light-gray dark:border-b-light-gray-dark relative";
  const highlightClass = isMatchTeam && fallbackClass ? ` ${fallbackClass}` : "";
  return (
    <tr className={baseRowClass + highlightClass} style={highlightStyle}>
      <td className="w-12 text-left pl-3 pr-3 py-2">
        <span className="text-left text-xs text-font dark:text-font-dark">{standing.placement}</span>
      </td>
      <td className="py-2">
        <button type="button" onClick={onTeamClick} className="text-left flex flex-row items-center justify-start">
          <div className="flex flex-row items-center justify-start">
            <EmblemImage imageUrl={standing.team.emblemUrl} width={24} className="mr-2" />
            <span className="text-sm text-font dark:text-font-dark hover:text-primary cursor-pointer">
              {showIds && isAdmin(user) ? `[${standing.team.id}]` : ""}
              {showTranslated(standing.team.name, l)}
            </span>
          </div>
        </button>
      </td>
      <td className="text-right py-2 px-1 text-sm text-font dark:text-font-dark w-12">{standing.wins}</td>
      <td className="text-right py-2 px-1 text-sm text-font dark:text-font-dark w-12">{standing.losses}</td>
      <td className="text-right py-2 px-1 text-sm text-font dark:text-font-dark w-12">{standing.draws}</td>
      <td className="text-right py-2 px-1 text-sm text-font dark:text-font-dark w-12">{standing.goalsOut}</td>
      <td className="text-right py-2 px-1 text-sm text-font dark:text-font-dark w-12">{standing.goalsIn}</td>
      <td className="text-right py-2 px-1 text-sm text-font dark:text-font-dark w-12">{standing.matchesPlayed}</td>
      <td className="text-right py-2 px-1 text-sm text-font dark:text-font-dark w-12">
        {standing.goalsOut - standing.goalsIn}
      </td>
      <td className="text-right py-2 pl-1 pr-4 text-sm text-font dark:text-font-dark font-bold w-12">
        {standing.points}
      </td>
    </tr>
  );
};

type TableProps = ConnectedProps<typeof connector>;

const Table: React.FC<TableProps> = ({
                                       leagueId,
                                       standing,
                                       getStanding,
                                       team1Id,
                                       team2Id
                                     }) => {
  const { l } = useContext(ClavaContext);
  const navigate = useNavigate();
  useEffect(() => {
    getStanding(leagueId);
  }, [getStanding, leagueId]);
  const selectedStanding = useMemo(() => {
    if (!standing || !standing.standings) return undefined;
    const st = standing.standings[MatchLocationEnum.ALL];
    if (st) {
      return [...st].sort((a, b) => a.placement - b.placement);
    }
    return undefined;
  }, [standing]);

  const handleTeamClick = useCallback(
    (teamId: IDType) => {
      navigate(pathTeamView(teamId, "table"));
    },
    [navigate]
  );
  return (
    <RoundedRectangle
        className="bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark"
        hasBorder
        hasTitle
        titleType="upCenter"
        titleKey="table"
      >
        <div className="table-container mt-4">
          {selectedStanding ? (
            <div className="min-w-full overflow-hidden rounded-b-xl">
              <table className="min-w-full table-fixed">
                <thead>
                <tr>
                  <th
                    className="w-12 text-left pl-3 pr-3 py-2 text- text-font dark:text-font-dark font-medium">{translate("tablePos", l)}</th>
                  <th
                    className="text-left py-2 text-sm text-font dark:text-font-dark font-medium">{translate("teams", l)}</th>
                  <th
                    className="text-right py-2 px-1 text-sm text-font dark:text-font-dark font-medium w-12">{translate("wins", l)}</th>
                  <th
                    className="text-right py-2 px-1 text-sm text-font dark:text-font-dark font-medium w-12">{translate("losses", l)}</th>
                  <th
                    className="text-right py-2 px-1 text-sm text-font dark:text-font-dark font-medium w-12">{translate("draws", l)}</th>
                  <th
                    className="text-right py-2 px-1 text-sm text-font dark:text-font-dark font-medium w-12">{translate("goalsOut", l)}</th>
                  <th
                    className="text-right py-2 px-1 text-sm text-font dark:text-font-dark font-medium w-12">{translate("goalsIn", l)}</th>
                  <th className="text-right py-2 px-1 text-sm text-font dark:text-font-dark font-medium w-12">
                    {translate("matchesShort", l)}
                  </th>
                  <th className="text-right py-2 px-1 text-sm text-font dark:text-font-dark font-medium w-12">
                    {translate("difference", l)}
                  </th>
                  <th className="text-right py-2 pl-1 pr-4 text-sm text-font dark:text-font-dark font-medium w-12">
                    {translate("points", l)}
                  </th>
                </tr>
                </thead>
                <tbody>
                {selectedStanding.map((st) => (
                  <Line
                    standing={st}
                    key={`stand${st.team.id}`}
                    isMatchTeam={st.team.id === team1Id || st.team.id === team2Id}
                    onTeamClick={() => handleTeamClick(st.team.id)}
                  />
                ))}
                </tbody>
              </table>
            </div>
          ) : (
            <span className="text-font dark:text-font-dark">{translate("loading", l)}</span>
          )}
        </div>
      </RoundedRectangle>
  );
};

export default connector(Table);
// r elo ad
